import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, BarChart3 } from 'lucide-react'
import { signalsApi } from '../api/client'
import { useWebSocketEvent } from '../hooks/useWebSocket'
import type { WebSocketHookReturn } from '../hooks/useWebSocket'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import './SignalPipeline.css'

interface SignalPipelineProps {
  ws: WebSocketHookReturn
}

const STAGES = [
  { key: 'received', label: 'Received', cls: 'stage-received' },
  { key: 'ordered', label: 'Ordered', cls: 'stage-ordered' },
  { key: 'executed', label: 'Executed', cls: 'stage-executed' },
  { key: 'rejected', label: 'Rejected', cls: 'stage-rejected' },
] as const

type PipelineView = 'session' | 'all'
const VIEW_STORAGE_KEY = 'signal-pipeline-view'

function formatSessionDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

export function SignalPipeline({ ws }: SignalPipelineProps) {
  const queryClient = useQueryClient()
  const [view, setView] = useState<PipelineView>(() => {
    try {
      return localStorage.getItem(VIEW_STORAGE_KEY) === 'all' ? 'all' : 'session'
    } catch {
      return 'session'
    }
  })
  const selectView = useCallback((v: PipelineView) => {
    setView(v)
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v)
    } catch {
      // localStorage unavailable (private mode) — view just won't persist
    }
  }, [])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['signal-pipeline'],
    queryFn: signalsApi.getPipeline,
    refetchInterval: 10000,
  })

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['signal-pipeline'] })
  }, [queryClient])
  useWebSocketEvent(ws, 'new_signal', invalidate)
  useWebSocketEvent(ws, 'signal_executed', invalidate)
  useWebSocketEvent(ws, 'signal_expired', invalidate)

  const session = data?.session
  const active = view === 'session' ? session : data
  const received = active?.received ?? 0
  const counts: Record<string, number> = {
    received,
    ordered: active?.ordered ?? 0,
    executed: active?.executed ?? 0,
    rejected: active?.rejected ?? 0,
  }
  const rejectReasons = active?.reject_reasons ?? []
  const denom = Math.max(received, 1)
  const isEmpty = received === 0
  const cumulativeHasData = (data?.received ?? 0) > 0
  const { expanded, toggle, scrollClassName } = useExpandablePanel('signal-pipeline')

  const footnote =
    view === 'session' && session
      ? `${session.is_current ? 'Session' : 'Last session'} · ${formatSessionDate(session.session_date)}`
      : 'Cumulative since last reset'

  return (
    <div className="card signal-pipeline-card">
      <div className="card-header">
        <h3 className="card-title">
          <Activity size={20} className="icon" />
          Signal Pipeline
        </h3>
        <div className="card-header-right">
          {!isEmpty && <span className="card-badge">{received} received</span>}
          {data && (
            <div className="view-toggle" role="group" aria-label="Count window">
              <button
                aria-pressed={view === 'session'}
                className={view === 'session' ? 'active' : ''}
                onClick={() => selectView('session')}
              >
                Today
              </button>
              <button
                aria-pressed={view === 'all'}
                className={view === 'all' ? 'active' : ''}
                onClick={() => selectView('all')}
              >
                All
              </button>
            </div>
          )}
          {rejectReasons.length > 0 && (
            <ExpandToggle expanded={expanded} onToggle={toggle} />
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-text">Failed to load pipeline</div>
        </div>
      ) : isEmpty ? (
        <div className="empty-state">
          <div className="empty-icon"><BarChart3 size={32} aria-hidden="true" /></div>
          <div className="empty-text">
            {view === 'session' && cumulativeHasData
              ? 'No signals this session yet'
              : 'No signals processed yet'}
          </div>
        </div>
      ) : (
        <>
          <div className="pipeline-funnel">
            {STAGES.map((s) => (
              <div className="funnel-row" key={s.key}>
                <span className="funnel-label">{s.label}</span>
                <div className="funnel-track">
                  <div
                    className={`funnel-fill ${s.cls}`}
                    style={{ width: `${Math.min(100, Math.round((counts[s.key] / denom) * 100))}%` }}
                  />
                </div>
                <span className="funnel-value mono">{counts[s.key]}</span>
              </div>
            ))}
          </div>

          {rejectReasons.length > 0 && (
            <div className={scrollClassName}>
              <div className="reject-reasons">
                <div className="reject-reasons-label">Why rejected</div>
                {rejectReasons.map((r) => (
                  <div className="reject-row" key={r.code}>
                    <span className="reject-label">{r.label}</span>
                    <span className="reject-count mono">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pipeline-footnote">{footnote}</div>
        </>
      )}
    </div>
  )
}
